import { createSlice, createAsyncThunk, PayloadAction, Draft } from '@reduxjs/toolkit';
import { Message, Conversation } from '../types/message';
import { apiService } from '../services/api';
import { notificationService } from '../services/notification';
import MessageTracer from '../utils/messageTracer';

interface MessagesState {
  conversations: Conversation[];
  activeConversation: string | null;
  messages: Record<string, Message[]>;
  messagesLoadMode: Record<string, '24h' | 'all'>;
  loading: boolean;
  sending: boolean;
  error: string | null;
  totalUnreadCount: number;
  shouldRefetchConversations: boolean;
}

const initialState: MessagesState = {
  conversations: [],
  activeConversation: null,
  messages: {},
  messagesLoadMode: {},
  loading: false,
  sending: false,
  error: null,
  totalUnreadCount: 0,
  shouldRefetchConversations: false,
};

// Async thunks
export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async () => {
    console.log('[MessagesSlice] 🔄 Fetching conversations from API...');
    const response = await apiService.getConversations();
    console.log('[MessagesSlice] ✅ Conversations fetched:', response.length, 'conversations');
    return response;
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async (
    payload: {
      conversationId?: string;
      search?: string;
      range?: '24h' | 'all';
      before?: string;
    }
  ) => {
    const { conversationId, search, range, before } = payload;
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[MessagesSlice] 📡 fetchMessages called:', { conversationId, search, range, before });
    }
    const options = (range || before) ? { range, before } : undefined;
    const response = await apiService.getMessages(conversationId, search, options);
    const messages = response.results || response;
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[MessagesSlice] ✅ fetchMessages response:', {
        conversationId,
        messageCount: messages.length,
        firstMessage: messages[0] ? messages[0].content?.substring(0, 50) : 'none',
        range
      });
    }
    return { conversationId, messages, range };
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  // Mirrors apiService.sendMessage's accepted payload.
  async (data: {
    content: string;
    receiver_id?: number;
    group_id?: number;
    message_type?: string;
    hospital_id?: number;
    urgency_level?: 'routine' | 'urgent' | 'critical';
    urgency_reason?: string;
    is_urgent?: boolean;
    priority?: 'normal' | 'urgent' | 'critical';
    _traceId?: string; // Trace ID for message flow tracking
  }) => {
    const response = await apiService.sendMessage(data);
    return response;
  }
);

export const markMessageRead = createAsyncThunk(
  'messages/markMessageRead',
  async (messageId: number) => {
    await apiService.markMessageRead(messageId);
    return messageId;
  }
);

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    // null clears the active conversation (e.g. when it has no messages yet)
    setActiveConversation: (state: Draft<MessagesState>, action: PayloadAction<string | null>) => {
      const conversationId = action.payload;
      state.activeConversation = conversationId;
      
      // Clear unread count for this conversation and update total
      const conversationIndex = state.conversations.findIndex(conv => conv.id === conversationId);
      if (conversationIndex >= 0) {
        const unreadCount = state.conversations[conversationIndex].unread_count || 0;
        state.conversations[conversationIndex].unread_count = 0;
        // Subtract from total unread count
        state.totalUnreadCount = Math.max(0, state.totalUnreadCount - unreadCount);
      }
    },
    markConversationAsRead: (state: Draft<MessagesState>, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      const conversationIndex = state.conversations.findIndex(conv => conv.id === conversationId);
      if (conversationIndex >= 0) {
        const unreadCount = state.conversations[conversationIndex].unread_count || 0;
        state.conversations[conversationIndex].unread_count = 0;
        // Subtract from total unread count
        state.totalUnreadCount = Math.max(0, state.totalUnreadCount - unreadCount);
      }
    },
    clearRefetchFlag: (state: Draft<MessagesState>) => {
      state.shouldRefetchConversations = false;
    },
    receiveMessage: (state: Draft<MessagesState>, action: PayloadAction<{ message: Message; currentUserId: number; source?: 'websocket' | 'api' }>) => {
      const { message, currentUserId, source = 'websocket' } = action.payload;
      const traceId = (message as any)._traceId as string | undefined;
      let conversationId: string;
      
      MessageTracer.logStep(traceId || '', 'receiveMessage-called', {
        messageId: message.id,
        isOptimistic: (message as any).isOptimistic,
        source,
        content: message.content?.substring(0, 50),
        senderId: message.sender?.id,
        receiverId: message.receiver?.id,
        isConfirmation: (message as any)._isConfirmation
      });
      
      const explicitConversationId = (message as any)._conversationId as string | undefined;
      if (explicitConversationId) {
        conversationId = explicitConversationId;
      } else if ('group' in message && message.group && typeof message.group === 'object' && 'id' in message.group) {
        // Group message
        conversationId = `group_${message.group.id}`;
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('Group message, conversationId:', conversationId);
        }
      } else {
        // Direct message - use the OTHER participant's ID as conversation ID
        if (message.sender.id === currentUserId) {
          // I sent this message, so the conversation is with the receiver
          conversationId = message.receiver?.id.toString() || message.sender.id.toString();
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('Message I sent, conversationId:', conversationId);
          }
        } else {
          // I received this message, so the conversation is with the sender
          conversationId = message.sender.id.toString();
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('Message I received, conversationId:', conversationId);
          }
        }
      }

      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      
      // Insert message in chronological order
      const messages = state.messages[conversationId];
      const messageTime = new Date(message.timestamp).getTime();
      
      console.log('[MessagesSlice] 📊 Current state before processing:', {
        conversationId,
        currentMessageCount: messages.length,
        currentMessageIds: messages.map(m => ({ id: m.id, isOptimistic: (m as any).isOptimistic, content: m.content?.substring(0, 30) })),
        incomingMessageId: message.id,
        incomingIsOptimistic: (message as any).isOptimistic
      });
      
      // Find the correct position to insert the message
      let insertIndex = messages.length;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (new Date(messages[i].timestamp).getTime() <= messageTime) {
          insertIndex = i + 1;
          break;
        }
        insertIndex = i;
      }
      
      const isConfirmation = (message as any)._isConfirmation;
      const isSender = message.sender.id === currentUserId;
      const isGroupMessage = !!message.group;

      // CRITICAL: Check for existing message by ID FIRST (before removing optimistic)
      // This prevents duplicates when HTTP API response arrives before WebSocket confirmation
      // Use strict comparison with type coercion to handle string/number ID mismatches
      const incomingMessageId = message.id;
      const existingMessage = messages.find(m => {
        const existingId = m.id;
        // Compare both as strings and numbers to handle type mismatches
        return existingId === incomingMessageId || 
               String(existingId) === String(incomingMessageId) ||
               Number(existingId) === Number(incomingMessageId);
      });
      
      if (existingMessage) {
        console.log('[MessagesSlice] ✅ DUPLICATE PREVENTION: Message already exists (by ID), updating instead of duplicating:', {
          messageId: message.id,
          messageIdType: typeof message.id,
          existingId: existingMessage.id,
          existingIdType: typeof existingMessage.id,
          source,
          isConfirmation,
          existingIsOptimistic: (existingMessage as any).isOptimistic,
          messageCountBefore: messages.length,
          messageIdsBefore: messages.map(m => ({ id: m.id, type: typeof m.id }))
        });
        
        MessageTracer.markDuplicate(traceId || '', message.id);
        MessageTracer.logStep(traceId || '', 'duplicate-by-id', {
          messageId: message.id,
          source,
          isConfirmation,
          existingIsOptimistic: (existingMessage as any).isOptimistic,
          messageCountBefore: messages.length
        });
        // Update existing message properties if needed (e.g., delivery status, urgency)
        Object.assign(existingMessage, message);
        (existingMessage as any).isOptimistic = false;
        
        // If there's an optimistic message with different ID, remove it
        if (isSender) {
          const optimisticIndex = messages.findIndex(existing => 
            (existing as any).isOptimistic &&
            existing.sender.id === currentUserId &&
            existing.content === message.content &&
            (existing.message_type || 'text') === (message.message_type || 'text') &&
            (message.group ? existing.group?.id === message.group.id : !existing.group) &&
            existing.id !== message.id // Make sure it's not the same message
          );
          
          if (optimisticIndex !== -1) {
            console.log('[MessagesSlice] 🗑️ Removing orphaned optimistic message:', {
              optimisticId: messages[optimisticIndex].id,
              realId: message.id,
              optimisticIndex
            });
            messages.splice(optimisticIndex, 1);
            console.log('[MessagesSlice] 📊 After removing optimistic:', {
              messageCount: messages.length,
              messageIds: messages.map(m => m.id)
            });
          }
        }
        
        MessageTracer.logStep(traceId || '', 'duplicate-prevented', {
          messageId: message.id,
          finalMessageCount: messages.length
        });
        return;
      }

      // If no existing message by ID, check for optimistic message to replace
      if (isSender) {
        const optimisticIndex = messages.findIndex(existing => 
          (existing as any).isOptimistic &&
          existing.sender.id === currentUserId &&
          existing.content === message.content &&
          (existing.message_type || 'text') === (message.message_type || 'text') &&
          (message.group ? existing.group?.id === message.group.id : !existing.group)
        );

        if (optimisticIndex !== -1) {
          MessageTracer.logStep(traceId || '', 'replace-optimistic', {
            optimisticId: messages[optimisticIndex].id,
            confirmedId: message.id,
            source,
            optimisticIndex
          });
          MessageTracer.associateMessageId(traceId || '', message.id);
          // Replace optimistic message with real message at the same position
          messages[optimisticIndex] = message;
          (message as any).isOptimistic = false;
          console.log('[MessagesSlice] ✅ After replacement:', {
            messageCount: messages.length,
            messageIds: messages.map(m => m.id)
          });
          return; // Don't add again, we replaced it
        } else {
          console.log('[MessagesSlice] ℹ️ No optimistic message found to replace:', {
            messageId: message.id,
            isSender,
            messageCount: messages.length,
            optimisticMessages: messages.filter(m => (m as any).isOptimistic).map(m => ({ id: m.id, content: m.content?.substring(0, 30) }))
          });
        }
      }
      
      // SIMPLIFIED DEDUPLICATION: Allow messages based on source and context
      
      // Determine if we should add this message
      let shouldAddMessage = false;
      
      if (source === 'api') {
        // API responses: Always add (optimistic updates and sender feedback)
        shouldAddMessage = true;
      } else if (source === 'websocket') {
        // Allow WebSocket messages for both sender and receiver.
        // Deduplication is already handled above by message ID and optimistic removal.
        shouldAddMessage = true;
      }
      
      if (!shouldAddMessage) {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('Skipping message - would create duplication:', { 
            messageId: message.id, 
            source, 
            isSender, 
            isGroupMessage 
          });
        }
        return;
      }
      
      // Final defensive check before adding - ensure no duplicate by ID (handles type mismatches)
      const incomingMessageIdFinal = message.id;
      const finalDuplicateCheck = messages.find(m => {
        const existingId = m.id;
        // Compare both as strings and numbers to handle type mismatches
        return existingId === incomingMessageIdFinal || 
               String(existingId) === String(incomingMessageIdFinal) ||
               (Number(existingId) === Number(incomingMessageIdFinal) && !isNaN(Number(existingId)) && !isNaN(Number(incomingMessageIdFinal)));
      });
      
      if (finalDuplicateCheck) {
        console.warn('[MessagesSlice] ⚠️ DUPLICATE DETECTED at final check - skipping add:', {
          messageId: message.id,
          messageIdType: typeof message.id,
          existingId: finalDuplicateCheck.id,
          existingIdType: typeof finalDuplicateCheck.id,
          source,
          messageCount: messages.length,
          allMessageIds: messages.map(m => ({ id: m.id, type: typeof m.id }))
        });
        MessageTracer.markDuplicate(traceId || '', message.id);
        MessageTracer.logStep(traceId || '', 'duplicate-prevented-final-check', {
          messageId: message.id,
          finalMessageCount: messages.length
        });
        return;
      }
      
      console.log('[MessagesSlice] ➕ ADDING new message (not a duplicate, not replacing optimistic):', {
        messageId: message.id,
        messageIdType: typeof message.id,
        source,
        insertIndex,
        messageCountBefore: messages.length,
        messageIdsBefore: messages.map(m => ({ id: m.id, type: typeof m.id }))
      });
      MessageTracer.logStep(traceId || '', 'adding-new-message', {
        messageId: message.id,
        source,
        insertIndex,
        messageCountBefore: messages.length
      });
      MessageTracer.associateMessageId(traceId || '', message.id);
      messages.splice(insertIndex, 0, message);
      console.log('[MessagesSlice] ✅ After adding new message:', {
        messageCount: messages.length,
        messageIds: messages.map(m => ({ id: m.id, type: typeof m.id }))
      });
      MessageTracer.logStep(traceId || '', 'message-added', {
        messageId: message.id,
        finalMessageCount: messages.length
      });
      
      // Update or create conversation for BOTH sent and received messages
      const conversationIndex = state.conversations.findIndex(conv => conv.id === conversationId);
      const isSentByMe = message.sender.id === currentUserId;
      
      if (conversationIndex >= 0) {
        // Update existing conversation
        state.conversations[conversationIndex].last_message = message;
        
        // Only increment unread count for messages not sent by current user
        if (!isSentByMe && !isConfirmation) {
          state.conversations[conversationIndex].unread_count += 1;
          state.totalUnreadCount += 1;
        }
        
        // Update conversation name to "AI Assistant" if this is an AI-delegated message
        if (message.is_ai_delegated && !message.group) {
          state.conversations[conversationIndex].name = 'AI Assistant';
        }
      } else {
        // Create new conversation for first-time messages (both sent and received)
        let conversationName: string;
        let participants: any[] = [];
        
        if (message.group) {
          conversationName = message.group.name || 'Group Chat';
        } else if (message.is_ai_delegated) {
          conversationName = 'AI Assistant';
          participants = [message.sender];
        } else if (isSentByMe && message.receiver) {
          // I sent this message - use receiver's name
          conversationName = message.receiver.first_name && message.receiver.last_name
            ? `${message.receiver.first_name} ${message.receiver.last_name}`
            : message.receiver.username || `User ${message.receiver.id}`;
          participants = [message.receiver];
        } else {
          // I received this message - use sender's name
          conversationName = message.sender.first_name && message.sender.last_name
            ? `${message.sender.first_name} ${message.sender.last_name}`
            : message.sender.username || `User ${message.sender.id}`;
          participants = [message.sender];
        }
          
        const newConversation: Conversation = {
          id: conversationId,
          type: message.group ? 'group' : 'direct',
          name: conversationName,
          participants: participants,
          unread_count: isSentByMe || isConfirmation ? 0 : 1,
          last_message: message,
          messages_count: 1
        };
        
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[MessagesSlice] 🆕 Creating new conversation in sidebar:', {
            conversationId,
            name: conversationName,
            isSentByMe,
            receiverId: message.receiver?.id,
            senderId: message.sender.id,
            participants: participants
          });
        }
        
        // Add to the beginning of the conversations list
        state.conversations.unshift(newConversation);
        
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[MessagesSlice] ✅ New conversation added to sidebar via receiveMessage:', {
            conversationId,
            totalConversations: state.conversations.length
          });
        }
        
        // Only increment total unread for received messages
        if (!isSentByMe && !isConfirmation) {
          state.totalUnreadCount += 1;
        }
      }
      
      // Show push notification only for messages not sent by current user
      // Skip notifications and unread count for confirmations (sender feedback)
      if (!isSentByMe && !isConfirmation) {
          // IMPORTANT: Skip regular notifications for urgent/critical messages
          // as they are already handled by WebSocket service with proper styling
          const isUrgentOrCritical = message.urgency_level && message.urgency_level !== 'routine';
          
          if (!isUrgentOrCritical) {
            if (message.group && message.group.name) {
              notificationService.showGroupMessageNotification(
                message.group.name,
                message.content,
                conversationId
              );
            } else {
              const senderName = message.sender.first_name && message.sender.last_name
                ? `${message.sender.first_name} ${message.sender.last_name}`
                : message.sender.username || `User ${message.sender.id}`;
              notificationService.showMessageNotification(
                senderName,
                message.content,
                conversationId
              );
            }
          }
        }
    },
    clearMessages: (state: Draft<MessagesState>) => {
      state.messages = {};
      state.conversations = [];
      state.activeConversation = null;
    },
    setLoading: (state: Draft<MessagesState>, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSending: (state: Draft<MessagesState>, action: PayloadAction<boolean>) => {
      state.sending = action.payload;
    },
    setError: (state: Draft<MessagesState>, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    markMessageSendFailed: (
      state: Draft<MessagesState>,
      action: PayloadAction<{
        conversationId?: string;
        optimisticId?: number;
        clientMessageId?: string;
        error?: string;
      }>
    ) => {
      const { conversationId, optimisticId, clientMessageId, error } = action.payload;
      const conversationIds = conversationId ? [conversationId] : Object.keys(state.messages);

      for (const currentConversationId of conversationIds) {
        const conversationMessages = state.messages[currentConversationId];
        if (!conversationMessages) {
          continue;
        }

        const failedMessage = conversationMessages.find(existing => {
          const idMatches =
            optimisticId !== undefined &&
            (existing.id === optimisticId || String(existing.id) === String(optimisticId));
          const clientIdMatches =
            !!clientMessageId && existing.client_message_id === clientMessageId;

          return idMatches || clientIdMatches;
        });

        if (!failedMessage) {
          continue;
        }

        failedMessage.delivery_status = 'failed';
        (failedMessage as any).isOptimistic = false;
        break;
      }

      state.error = error || 'Failed to send message';
    },
    addVirtualConversation: (state: Draft<MessagesState>, action: PayloadAction<Conversation>) => {
      const virtualConversation = action.payload;
      // Only add if it doesn't already exist
      const existingIndex = state.conversations.findIndex(conv => conv.id === virtualConversation.id);
      if (existingIndex === -1) {
        // Add to the beginning of the list
        state.conversations.unshift(virtualConversation);
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[MessagesSlice] ✅ Virtual conversation added to sidebar:', {
            conversationId: virtualConversation.id,
            name: virtualConversation.name,
            type: virtualConversation.type,
            totalConversations: state.conversations.length
          });
        }
      } else {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[MessagesSlice] ⚠️ Virtual conversation already exists, not adding:', virtualConversation.id);
        }
      }
    },
    handleGroupUpdate: (state: Draft<MessagesState>, action: PayloadAction<{ action: string; group: any; details?: any }>) => {
      const { action: updateAction, group, details } = action.payload;
      console.log('[DEBUG-MessagesSlice] 📢 handleGroupUpdate called:', { 
        updateAction, 
        group, 
        details,
        currentConversationsCount: state.conversations.length 
      });
      
      if (updateAction === 'created') {
        // Create new conversation object for the group
        const newConversation: Conversation = {
          id: `group_${group.id}`,
          type: 'group',
          name: group.name,
          participants: [], // Will be populated when needed
          unread_count: 0,
          last_message: undefined,
          member_count: group.member_count
        };
        
        console.log('[DEBUG-MessagesSlice] 🆕 Creating new conversation:', newConversation);
        console.log('[DEBUG-MessagesSlice] Current conversations:', state.conversations.map(c => ({ id: c.id, name: c.name })));
        
        // Add to conversations if not already present
        const existingConversation = state.conversations.find(conv => conv.id === newConversation.id);
        if (!existingConversation) {
          console.log('[DEBUG-MessagesSlice] ✅ Adding new conversation to list');
          state.conversations.unshift(newConversation);
          console.log('[DEBUG-MessagesSlice] ✅ Conversation added! New count:', state.conversations.length);
          console.log('[DEBUG-MessagesSlice] Updated conversations:', state.conversations.map(c => ({ id: c.id, name: c.name })));
        } else {
          console.log('[DEBUG-MessagesSlice] ⚠️ Conversation already exists, skipping:', existingConversation);
        }
      } else if (updateAction === 'member_added' || updateAction === 'member_removed') {
        // Find and update the group conversation
        const groupConversationIndex = state.conversations.findIndex(
          conv => conv.type === 'group' && conv.id === `group_${group.id}`
        );
        
        if (groupConversationIndex !== -1) {
          // Update member count
          if (details && details.member_count !== undefined) {
            state.conversations[groupConversationIndex].member_count = details.member_count;
          }
          
          // You could also update participants list if needed
          // state.conversations[groupConversationIndex].participants = group.members;
        }
      }
    },
    updateTotalUnreadCount: (state: Draft<MessagesState>, action: PayloadAction<number>) => {
      state.totalUnreadCount = Math.max(0, action.payload);
    },
    decrementUnreadCount: (state: Draft<MessagesState>, action: PayloadAction<number>) => {
      state.totalUnreadCount = Math.max(0, state.totalUnreadCount - (action.payload || 1));
    },
    updateMessageAcknowledgment: (state: Draft<MessagesState>, action: PayloadAction<{
      messageId: number;
      acknowledgment: any;
    }>) => {
      const { messageId, acknowledgment } = action.payload;
      
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('REDUX_ACK: Updating message acknowledgment', {
          messageId,
          acknowledgmentData: acknowledgment,
          timestamp: new Date().toISOString()
        });
      }
      
      let messageFound = false;
      let messagesBefore: any = {};
      let messagesAfter: any = {};
      
      // Find and update the message across all conversations
      Object.values(state.messages).forEach((conversationMessages: Message[]) => {
        const message = conversationMessages.find(m => m.id === messageId);
        if (message) {
          messageFound = true;
          // Store before state
          messagesBefore = {
            manually_acknowledged_at: message.manually_acknowledged_at,
            is_manually_acknowledged: message.is_manually_acknowledged
          };
          
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('REDUX_ACK: Found message in Redux state', {
              messageId,
              beforeUpdate: messagesBefore
            });
          }
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('Redux: Found message to update', {
              messageId: message.id,
              oldManuallyAcknowledged: message.is_manually_acknowledged,
              newManuallyAcknowledged: acknowledgment.is_manually_acknowledged
            });
          }
          // Update delivery status based on acknowledgment
          if (acknowledgment.is_read) {
            message.delivery_status = 'read';
          } else if (acknowledgment.is_delivered) {
            message.delivery_status = 'delivered';
          }
          
          // Update manual acknowledgment fields for critical messages
          if (acknowledgment.manually_acknowledged_at) {
            message.manually_acknowledged_at = acknowledgment.manually_acknowledged_at;
            message.is_manually_acknowledged = true;
            if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
              console.log('REDUX_ACK: Updated manually_acknowledged_at', {
                messageId,
                manually_acknowledged_at: acknowledgment.manually_acknowledged_at
              });
            }
          }
          if (acknowledgment.is_manually_acknowledged !== undefined) {
            message.is_manually_acknowledged = acknowledgment.is_manually_acknowledged;
            if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
              console.log('REDUX_ACK: Updated is_manually_acknowledged', {
                messageId,
                is_manually_acknowledged: acknowledgment.is_manually_acknowledged
              });
            }
          }
          
          // Store after state
          messagesAfter = {
            manually_acknowledged_at: message.manually_acknowledged_at,
            is_manually_acknowledged: message.is_manually_acknowledged
          };
          
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('REDUX_ACK: Message update completed', {
              messageId,
              beforeUpdate: messagesBefore,
              afterUpdate: messagesAfter,
              changesSummary: {
                manually_acknowledged_at_changed: messagesBefore.manually_acknowledged_at !== messagesAfter.manually_acknowledged_at,
                is_manually_acknowledged_changed: messagesBefore.is_manually_acknowledged !== messagesAfter.is_manually_acknowledged
              }
            });
          }
          
          // Update acknowledgment count and timestamps if needed
          if (acknowledgment.read_at) {
            message.is_read = true;
            message.read_at = acknowledgment.read_at;
          }
          if (acknowledgment.delivered_at) {
            message.delivered_at = acknowledgment.delivered_at;
          }
        }
      });
    },
    handleBulkAcknowledgments: (state: Draft<MessagesState>, action: PayloadAction<{
      acknowledgments: any[];
    }>) => {
      const { acknowledgments } = action.payload;
      
      // Update multiple messages at once
      acknowledgments.forEach(ack => {
        Object.values(state.messages).forEach((conversationMessages: Message[]) => {
          const message = conversationMessages.find(m => m.id === ack.message_id);
          if (message) {
            // Update delivery status based on acknowledgment
            if (ack.is_read) {
              message.delivery_status = 'read';
            } else if (ack.is_delivered) {
              message.delivery_status = 'delivered';
            }
            
            // Update acknowledgment count and timestamps if needed
            if (ack.read_at) {
              message.is_read = true;
              message.read_at = ack.read_at;
            }
            if (ack.delivered_at) {
              message.delivered_at = ack.delivered_at;
            }
          }
        });
      });
    },
  },
  extraReducers: (builder) => {
    // Fetch conversations
    builder
      .addCase(fetchConversations.pending, (state: Draft<MessagesState>) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state: Draft<MessagesState>, action) => {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          const logPayload = {
            count: action.payload.length,
            unreadTotal: action.payload.reduce((sum: number, c: Conversation) => sum + (c.unread_count || 0), 0),
            conversations: action.payload.map((c: Conversation) => ({ 
              id: c.id, 
              name: c.name, 
              type: c.type,
              unread_count: c.unread_count,
              member_count: c.member_count,
              department_channel_name: c.department_channel_name
            }))
          };
          console.log('[MessagesSlice] 📥 fetchConversations.fulfilled:', logPayload);
          const groups = action.payload.filter(c => c.type === 'group');
          console.log('[MessagesSlice] 🔍 Group conversations debug:', groups.map(g => ({
            id: g.id,
            name: g.name,
            member_count: g.member_count,
            department_channel_name: g.department_channel_name,
            unread_count: g.unread_count
          })));
          const equipmentGroups = groups.filter(g => g.name.includes('Equipment Request'));
          console.log('[MessagesSlice] 🔍 Equipment Request groups from API:', equipmentGroups.map(g => ({
            id: g.id,
            name: g.name,
            member_count: g.member_count,
            department_channel_name: g.department_channel_name,
            unread_count: g.unread_count
          })));
        }
        state.loading = false;

        // Preserve in-session virtual conversations (a chat the user just started but
        // hasn't sent a message in yet). The backend never returns these, so without
        // this a background refresh would drop the chat the user is actively composing.
        // They live only in memory, so a real page refresh still clears them.
        const preservedVirtuals = state.conversations.filter(
          conv => conv.is_virtual && !action.payload.some(c => String(c.id) === String(conv.id))
        );

        // Replace with the API response, keeping any active virtual chat on top.
        state.conversations = [...preservedVirtuals, ...action.payload];

        // Clear activeConversation only if it's neither in the fetched list nor a
        // preserved virtual chat. This prevents showing stale/uninitiated chats after
        // a refresh without wiping the new chat the user just opened.
        if (state.activeConversation) {
          const activeExists = state.conversations.some(
            conv => String(conv.id) === String(state.activeConversation)
          );
          if (!activeExists) {
            console.log('[MessagesSlice] ⚠️ Active conversation not in fetched list - clearing it');
            state.activeConversation = null;
          }
        }
        
        state.shouldRefetchConversations = false; // Clear refetch flag after successful fetch
        // Calculate total unread count
        state.totalUnreadCount = action.payload.reduce((total: number, conv: Conversation) => {
          return total + (conv.unread_count || 0);
        }, 0);
      })
      .addCase(fetchConversations.rejected, (state: Draft<MessagesState>, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch conversations';
      })
      // Fetch messages
      .addCase(fetchMessages.pending, (state: Draft<MessagesState>) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state: Draft<MessagesState>, action) => {
        state.loading = false;
        const { conversationId, messages, range } = action.payload;
        
        console.log('[MessagesSlice] ✅ fetchMessages.fulfilled:', {
          conversationId,
          messageCount: messages.length,
          messageIds: messages.map((m: any) => m.id),
          range,
          messageDetails: messages.map((m: any) => ({
            id: m.id,
            senderId: m.sender?.id,
            receiverId: m.receiver?.id,
            content: m.content?.substring(0, 50),
            timestamp: m.timestamp
          })),
          timestamp: new Date().toISOString()
        });
        
        // Check for critical messages and their acknowledgment status
        const criticalMessages = messages.filter((msg: Message) => msg.urgency_level === 'critical');
        if (criticalMessages.length > 0) {
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('FETCH_MESSAGES: Critical messages acknowledgment status', {
              conversationId,
              criticalMessages: criticalMessages.map((msg: Message) => ({
                id: msg.id,
                content: msg.content.substring(0, 50) + '...',
                requires_manual_acknowledgment: msg.requires_manual_acknowledgment,
                manually_acknowledged_at: msg.manually_acknowledged_at,
                is_manually_acknowledged: msg.is_manually_acknowledged,
                sender_id: msg.sender?.id,
                receiver_id: msg.receiver?.id
              }))
            });
          }
        }
        
        if (conversationId) {
          if (range === 'all') {
            state.messages[conversationId] = [...messages].sort(
              (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            state.messagesLoadMode[conversationId] = 'all';
          } else {
            // Merge with existing messages (default 24h or load previous 24h)
            const existingMessages = state.messages[conversationId] || [];
            
            const existingMessagesMap = new Map<number, Message>();
            existingMessages.forEach((msg: Message) => {
              if (msg.id && !(msg as any).isOptimistic) {
                existingMessagesMap.set(msg.id, msg);
              }
            });
            
            messages.forEach((msg: Message) => {
              if (msg.id) {
                existingMessagesMap.set(msg.id, msg);
              }
            });
            
            existingMessages.forEach((msg: Message) => {
              if ((msg as any).isOptimistic) {
                const hasMatchingRealMessage = Array.from(existingMessagesMap.values()).some(realMsg => 
                  realMsg.sender.id === msg.sender.id &&
                  realMsg.content === msg.content &&
                  (realMsg.message_type || 'text') === (msg.message_type || 'text') &&
                  ((realMsg.group && msg.group && realMsg.group.id === msg.group.id) || 
                   (!realMsg.group && !msg.group && realMsg.receiver?.id === msg.receiver?.id))
                );
                if (!hasMatchingRealMessage && !existingMessagesMap.has(msg.id)) {
                  existingMessagesMap.set(msg.id, msg);
                }
              }
            });
            
            const mergedMessages = Array.from(existingMessagesMap.values()).sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            
            state.messages[conversationId] = mergedMessages;
            state.messagesLoadMode[conversationId] = '24h';
            
            MessageTracer.logStep('', 'fetchMessages-merged', {
              conversationId,
              existingCount: existingMessages.length,
              apiCount: messages.length,
              mergedCount: mergedMessages.length,
              duplicateCount: existingMessages.length + messages.length - mergedMessages.length
            });
            
            const messageIdSet = new Set<number>();
            const duplicates: number[] = [];
            mergedMessages.forEach(msg => {
              if (msg.id && messageIdSet.has(msg.id)) {
                duplicates.push(msg.id);
              } else if (msg.id) {
                messageIdSet.add(msg.id);
              }
            });
            if (duplicates.length > 0) {
              console.error('[MessagesSlice] ❌ DUPLICATES IN MERGED RESULT:', {
                conversationId,
                duplicates,
                mergedCount: mergedMessages.length
              });
            }
          }
        } else {
          state.messages = { [conversationId || 'global']: messages };
        }
      })
      .addCase(fetchMessages.rejected, (state: Draft<MessagesState>, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch messages';
      })
      // Send message
      .addCase(sendMessage.pending, (state: Draft<MessagesState>) => {
        state.sending = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state: Draft<MessagesState>, action) => {
        state.sending = false;
        // DEDUPLICATION FIX: Use receiveMessage action with 'api' source
        // This ensures consistent message handling and deduplication logic
        const sentMessage = action.payload;
        const traceId = (sentMessage as any)._traceId as string | undefined;
        
        if (sentMessage) {
          MessageTracer.logStep(traceId || '', 'sendMessage-fulfilled', {
            messageId: sentMessage.id,
            senderId: sentMessage.sender?.id,
            receiverId: sentMessage.receiver?.id,
            groupId: sentMessage.group?.id,
            content: sentMessage.content?.substring(0, 50)
          });
          MessageTracer.associateMessageId(traceId || '', sentMessage.id);
          
          // Get current user ID from auth state (assuming it's available in global state)
          // For now, we'll use the sender ID from the message
          const currentUserId = sentMessage.sender.id;
          
          // Handle message addition for sender feedback (avoiding circular reference)
          // Instead of calling receiveMessage, we'll handle this directly here
          let conversationId: string;
          if (sentMessage.group) {
            conversationId = `group_${sentMessage.group.id}`;
          } else if (sentMessage.receiver) {
            // For direct messages, use the receiver's ID as conversation ID
            // This matches how conversations are identified in get_conversations
            conversationId = sentMessage.receiver.id.toString();
          } else {
            // Fallback (shouldn't happen for direct messages)
            conversationId = sentMessage.sender.id.toString();
          }
          
          if (!state.messages[conversationId]) {
            state.messages[conversationId] = [];
          }
          
          const messages = state.messages[conversationId];
          
          console.log('[MessagesSlice] 📊 sendMessage.fulfilled - Current state:', {
            conversationId,
            messageId: sentMessage.id,
            existingMessagesCount: messages.length,
            existingMessageIds: messages.map(m => ({ id: m.id, isOptimistic: (m as any).isOptimistic, content: m.content?.substring(0, 30) }))
          });
          
          // First, check if message with same ID already exists (from WebSocket confirmation)
          const existingMessageById = messages.find(m => m.id === sentMessage.id);
          if (existingMessageById) {
            MessageTracer.markDuplicate(traceId || '', sentMessage.id);
            MessageTracer.logStep(traceId || '', 'duplicate-by-id-sendMessage', {
              messageId: sentMessage.id,
              existingIsOptimistic: (existingMessageById as any).isOptimistic,
              messageCount: messages.length
            });
            // Update existing message properties if needed
            Object.assign(existingMessageById, sentMessage);
            (existingMessageById as any).isOptimistic = false;
            MessageTracer.logStep(traceId || '', 'duplicate-prevented-sendMessage', {
              messageId: sentMessage.id,
              finalMessageCount: messages.length
            });
            return;
          }
          
          // Second, find and replace optimistic message (matching by content, sender, and timestamp)
          const optimisticIndex = messages.findIndex(existing => 
            (existing as any).isOptimistic &&
            existing.sender.id === currentUserId &&
            existing.content === sentMessage.content &&
            (existing.message_type || 'text') === (sentMessage.message_type || 'text') &&
            (sentMessage.group ? existing.group?.id === sentMessage.group.id : !existing.group)
          );
          
          if (optimisticIndex !== -1) {
            MessageTracer.logStep(traceId || '', 'replace-optimistic-sendMessage', {
              optimisticId: messages[optimisticIndex].id,
              confirmedId: sentMessage.id,
              optimisticIndex
            });
            // Replace optimistic message with real message
            messages[optimisticIndex] = sentMessage;
            (sentMessage as any).isOptimistic = false;
            MessageTracer.logStep(traceId || '', 'optimistic-replaced-sendMessage', {
              messageId: sentMessage.id,
              finalMessageCount: messages.length
            });
            // Don't add again - we replaced it
            return;
          } else {
            MessageTracer.logStep(traceId || '', 'no-optimistic-sendMessage', {
              messageId: sentMessage.id,
              messageCount: messages.length
            });
            // No optimistic message found, add message in chronological order
            const messageTime = new Date(sentMessage.timestamp).getTime();
            
            // Find the correct position to insert the message
            let insertIndex = messages.length;
            for (let i = messages.length - 1; i >= 0; i--) {
              if (new Date(messages[i].timestamp).getTime() <= messageTime) {
                insertIndex = i + 1;
                break;
              }
            }
            
            messages.splice(insertIndex, 0, sentMessage);
            MessageTracer.logStep(traceId || '', 'message-added-sendMessage', {
              messageId: sentMessage.id,
              insertIndex,
              finalMessageCount: messages.length
            });
          }
          
          // AUTO-RESTORE FIX: Only flag for refetch if conversation truly doesn't exist
          // Check with string comparison to handle type mismatches (string vs number IDs)
          const conversationExists = state.conversations.some(conv => 
            String(conv.id) === String(conversationId)
          );
          
          // Only set the flag if conversation doesn't exist AND we're not already refetching
          // This prevents unnecessary refetches when messages are sent to existing conversations
          if (!conversationExists && !state.shouldRefetchConversations) {
            console.log('[MessagesSlice] 🔄 Conversation not found in list, may have been auto-restored. Flagging for refetch.');
            // Signal that we need to refetch conversations (only once)
            state.shouldRefetchConversations = true;
          }
          // Don't clear the flag here - let it be cleared by fetchConversations.fulfilled
          // This prevents race conditions where multiple messages are sent quickly
        } else {
          console.warn('[MessagesSlice] ⚠️ sendMessage.fulfilled - no message in payload');
        }
      })
      .addCase(sendMessage.rejected, (state: Draft<MessagesState>, action) => {
        state.sending = false;
        state.error = action.error.message || 'Failed to send message';
      })
      // Mark message read
      .addCase(markMessageRead.fulfilled, (state: Draft<MessagesState>, action) => {
        const messageId = action.payload;
        // Update message read status in state
        Object.values(state.messages).forEach((conversationMessages: Message[]) => {
          const message = conversationMessages.find(m => m.id === messageId);
          if (message) {
            message.is_read = true;
          }
        });
      });
  },
});

export const { 
  setActiveConversation, 
  receiveMessage, 
  clearMessages, 
  setLoading, 
  setSending, 
  setError,
  markMessageSendFailed,
  addVirtualConversation,
  handleGroupUpdate,
  updateTotalUnreadCount,
  decrementUnreadCount,
  markConversationAsRead,
  clearRefetchFlag,
  updateMessageAcknowledgment,
  handleBulkAcknowledgments
} = messagesSlice.actions;

export default messagesSlice.reducer;
